/** Structural helpers behind the chart-of-accounts tree (tested without a DOM). */

export interface TreeNodeLike<T> {
  children: T[];
}

export interface FlatTreeNode<T> {
  node: T;
  level: number;
  hasChildren: boolean;
}

export interface TreeFlags {
  hasChildren: boolean;
  isGroup: boolean;
  isOffbalance: boolean;
  /** Only a group accepts children; a leaf is refused by the API too. */
  canHaveChildren: boolean;
}

export interface VisibleWindow {
  startIndex: number;
  rows: number;
  totalHeight: number;
  offsetY: number;
}

export function defaultId(node: { code?: unknown; id?: unknown }): string {
  if (typeof node.code === 'string') return node.code;
  if (typeof node.id === 'string') return node.id;
  return '';
}

/**
 * Depth-first walk of the branches the user has opened. Collapsed children are
 * not visited at all, which is what keeps a 127-account chart cheap to mount.
 */
export function flattenTree<T extends TreeNodeLike<T>>(
  roots: readonly T[],
  expanded: ReadonlySet<string>,
  getId: (node: T) => string = defaultId as (node: T) => string,
): FlatTreeNode<T>[] {
  const rows: FlatTreeNode<T>[] = [];
  const walk = (nodes: readonly T[], level: number): void => {
    for (const node of nodes) {
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      rows.push({ node, level, hasChildren });
      if (hasChildren && expanded.has(getId(node))) {
        walk(node.children, level + 1);
      }
    }
  };
  walk(roots, 0);
  return rows;
}

export function nodeFlags(node: {
  children?: unknown[];
  isGroup?: boolean;
  isOffbalance?: boolean;
}): TreeFlags {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isGroup = node.isGroup === true;
  return {
    hasChildren,
    isGroup,
    isOffbalance: node.isOffbalance === true,
    canHaveChildren: isGroup,
  };
}

export function visibleWindow(
  total: number,
  rowHeight: number,
  viewportHeight: number,
  overscan: number,
  scrollTop: number,
): VisibleWindow {
  const height = rowHeight > 0 ? rowHeight : 1;
  const visibleCount = Math.max(1, Math.ceil(viewportHeight / height));
  const lastFullStart = Math.max(0, total - visibleCount);
  const start = Math.min(lastFullStart, Math.max(0, Math.floor(scrollTop / height) - overscan));
  const rows = Math.max(0, Math.min(visibleCount + overscan * 2, total - start));
  return {
    startIndex: rows === 0 ? 0 : start,
    rows,
    totalHeight: total * height,
    offsetY: start * height,
  };
}
